// 轮询管理器 - 单例模式
import FJConstant from '../constants/FJConstant.js';
import { MAX_POLLING_ERROR_TIME, TASK_POLLING_INTERVAL } from '../config/dataConfig/FJGenerateData.js';
import FJUtil from '../util/FJUtil.js';
import FJNetworkStore from './FJNetworkStore.js';

const FJPollingManager = {
    isPolling: false,
    timer: null,
    eventIdList: [],
    callbacks: new Map(), // eventId -> callback
    errorTime: 0,
    waitingEndTime: null,
    failTaskCount: 0,
    allTaskCount: 0,

    // 添加新的轮询任务
    addPollingTask(eventIdList, callback, isUseFree = false) {
        const newEventIds = eventIdList.filter(id => !this.eventIdList.includes(id));

        // 添加新的eventId到列表
        this.eventIdList = [...new Set([...this.eventIdList, ...eventIdList])];

        // 为新的eventId注册回调
        newEventIds.forEach(eventId => {
            this.callbacks.set(eventId, { callback, isUseFree });
        });

        // 更新总任务数
        this.allTaskCount = this.eventIdList.length;

        // 如果当前没有在轮询，开始轮询
        if (!this.isPolling) {
            this.startPolling();
        }
    },

    // 开始轮询
    startPolling() {
        if (this.isPolling) return;

        this.isPolling = true;
        this.errorTime = 0;
        this.failTaskCount = 0;
        this.waitingEndTime = null;

        this._poll();
    },

    // 停止轮询
    stopPolling() {
        this.isPolling = false;
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    },

    // 清理完成的任务
    cleanupCompletedTasks(completedEventIds) {
        completedEventIds.forEach(eventId => {
            this.eventIdList = this.eventIdList.filter(id => id !== eventId);
            this.callbacks.delete(eventId);
        });

        // 如果没有剩余任务，停止轮询
        if (this.eventIdList.length === 0) {
            this.stopPolling();
        }
    },

    // 轮询逻辑
    async _poll() {
        if (!this.isPolling || this.eventIdList.length === 0) {
            this.stopPolling();
            return;
        }

        try {
            const result = await FJNetworkStore.getNewTaskState(this.eventIdList.join(','));
            let isStopPolling = false;
            let data = result;

            if (!data) {
                this.errorTime++;
                if (this.errorTime >= MAX_POLLING_ERROR_TIME) {
                    // 所有任务都失败
                    this.eventIdList.forEach(eventId => {
                        const callbackInfo = this.callbacks.get(eventId);
                        if (callbackInfo) {
                            callbackInfo.callback({
                                waitingEndTime: this.waitingEndTime,
                                type: FJConstant.ERROR,
                                eventId,
                            });
                        }
                    });
                    isStopPolling = true;
                } else {
                    this.timer = setTimeout(() => this._poll(), TASK_POLLING_INTERVAL);
                }
                return;
            }

            if (!Array.isArray(result)) {
                data = [result];
            }

            let allTaskStatus = '';
            const completedEventIds = [];

            await Promise.all(
                data.map(async item => {
                    if (item.soft_canceled) {
                        allTaskStatus = 'canceled';
                    } else {
                        switch (item.job_state) {
                            case 'succeeded':
                                if (item.out_put) {
                                    const list = item.out_put;
                                    const creationList = await FJUtil.parseList(list, {
                                        type: FJConstant.CREATIONS,
                                    });

                                    const callbackInfo = this.callbacks.get(item.event_id);
                                    if (callbackInfo) {
                                        callbackInfo.callback({
                                            type: FJConstant.SUCCESS,
                                            eventId: item.event_id,
                                            data: creationList,
                                        });
                                    }
                                    completedEventIds.push(item.event_id);
                                } else {
                                    this.failTaskCount++;
                                }
                                break;

                            case 'waiting':
                                (allTaskStatus === '' ||
                                    allTaskStatus === 'processing' ||
                                    allTaskStatus === 'canceled') &&
                                    (allTaskStatus = 'waiting');
                                break;

                            case 'processing':
                                allTaskStatus === '' && (allTaskStatus = 'processing');
                                break;

                            case 'canceled':
                                allTaskStatus = 'canceled';
                                break;
                            case 'timeout':
                            case 'failed': {
                                const callbackInfo = this.callbacks.get(item.event_id);
                                if (callbackInfo) {
                                    callbackInfo.callback({
                                        type: FJConstant.ERROR,
                                        eventId: item.event_id,
                                    });
                                }
                                this.failTaskCount++;
                                completedEventIds.push(item.event_id);
                                if (this.failTaskCount === this.allTaskCount) {
                                    allTaskStatus = 'failed';
                                }
                                break;
                            }
                            default:
                                break;
                        }
                    }
                }),
            );

            // 清理已完成的任务
            this.cleanupCompletedTasks(completedEventIds);

            if (allTaskStatus) {
                switch (allTaskStatus) {
                    case 'succeeded': {
                        isStopPolling = true;
                        // 更新积分信息
                        this.eventIdList.forEach(eventId => {
                            const callbackInfo = this.callbacks.get(eventId);
                            if (callbackInfo) {
                                FJUtil.updateAllCreditInfo(callbackInfo.isUseFree);
                            }
                        });
                        break;
                    }
                    case 'canceled':
                    case 'failed':
                        isStopPolling = true;
                        break;
                    default:
                        break;
                }
                this.errorTime = 0;
            }

            if (isStopPolling || this.eventIdList.length === 0) {
                this.stopPolling();
            } else {
                this.timer = setTimeout(() => this._poll(), TASK_POLLING_INTERVAL);
            }
        } catch (error) {
            // 发生错误时，通知所有剩余任务
            this.eventIdList.forEach(eventId => {
                const callbackInfo = this.callbacks.get(eventId);
                if (callbackInfo) {
                    callbackInfo.callback({
                        waitingEndTime: this.waitingEndTime,
                        type: FJConstant.ERROR,
                        eventId,
                    });
                }
            });
            this.stopPolling();
        }
    },
};

export default FJPollingManager;
